import { useState } from 'react';
import {
  Box, Typography, Chip, Collapse, Skeleton, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableBody, TableRow, TableCell, Link,
} from '@mui/material';
import { History as HistoryIcon, ExpandMore, ExpandLess, OpenInNew } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { auditService, AuditRow } from '../services/audit.service';

// ---------------------------------------------------------------------------
// Helpers (compact versions of the ones in AuditLogPage)
// ---------------------------------------------------------------------------

function userLabel(row: AuditRow): string {
  if (row.action === 'platform_status_change') return 'Facebook';
  const name = `${row.user_first_name || ''} ${row.user_last_name || ''}`.trim();
  return name || row.user_email || row.user_id || 'system';
}

// How the change was made. 'ui' is the normal case and gets no badge; anything else
// (MCP tool, API key, a background job) is worth flagging next to the actor.
function sourceLabel(row: AuditRow): string | null {
  switch (row.source) {
    case 'mcp': return row.mcp_client_name ? `MCP · ${row.mcp_client_name}` : 'MCP';
    case 'api': return row.api_key_name ? `API · ${row.api_key_name}` : 'API';
    case 'system': return 'System';
    default: return null;
  }
}

function actionColor(action: string): 'success' | 'info' | 'warning' | 'error' | 'default' {
  switch (action) {
    case 'create': return 'success';
    case 'resume': return 'success';
    case 'update': return 'info';
    case 'archive': return 'warning';
    case 'pause': return 'warning';
    case 'platform_status_change': return 'error';
    case 'delete': return 'error';
    default: return 'default';
  }
}

const ACTION_LABEL: Record<string, string> = { platform_status_change: 'on platform' };

// Keys whose value changed between old_values and new_values.
function changedKeys(row: AuditRow): string[] {
  if (row.action === 'delete') return [];
  const oldV = row.old_values || {};
  const newV = row.new_values || {};
  if (row.action === 'create') return Object.keys(newV);
  const keys = new Set([...Object.keys(oldV), ...Object.keys(newV)]);
  return Array.from(keys).filter((k) => JSON.stringify(oldV[k]) !== JSON.stringify(newV[k]));
}

function changesSummary(row: AuditRow): string {
  if (row.action === 'delete') return '(deleted)';
  return changedKeys(row).join(', ') || 'No field changes';
}

function renderValue(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// ---------------------------------------------------------------------------
// Timeline row
// ---------------------------------------------------------------------------

function HistoryRow({ row }: { row: AuditRow }) {
  const [open, setOpen] = useState(false);
  const keys = changedKeys(row);
  const oldV = row.old_values || {};
  const newV = row.new_values || {};
  const isCreate = row.action === 'create';
  const isDelete = row.action === 'delete';

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
          cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <IconButton size="small" sx={{ p: 0.2 }}>
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
        <Chip
          label={ACTION_LABEL[row.action] || row.action} size="small" variant="filled"
          color={actionColor(row.action)}
          sx={{ fontWeight: 500, minWidth: 72 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {userLabel(row)}
            </Typography>
            {sourceLabel(row) && (
              <Chip
                label={sourceLabel(row)} size="small" variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {changesSummary(row)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {new Date(row.created_at).toLocaleString()}
        </Typography>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
          {isDelete ? (
            <Typography variant="body2" color="text.secondary">(deleted)</Typography>
          ) : keys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No field changes.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Field</TableCell>
                  {!isCreate && <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Old</TableCell>}
                  <TableCell sx={{ fontWeight: 600, py: 0.5 }}>{isCreate ? 'Value' : 'New'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', py: 0.5 }}>{k}</TableCell>
                    {!isCreate && (
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', py: 0.5, wordBreak: 'break-all', color: 'text.secondary' }}>
                        {renderValue(oldV[k])}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', py: 0.5, wordBreak: 'break-all' }}>
                      {renderValue(newV[k])}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// EntityHistory — audit timeline for a single entity
// ---------------------------------------------------------------------------


/**
 * Opens the full audit log pre-filtered to this record. The inline timeline is
 * capped at 100 entries and scoped to this dialog; the audit page adds date
 * range, action filter and CSV export for the same record.
 */
function FullAuditLink({ entityType, entityId }: { entityType: string; entityId: string }) {
  return (
    <Box sx={{ textAlign: 'right', mt: 1 }}>
      <Link
        component={RouterLink}
        to={`/audit?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`}
        target="_blank"
        rel="noopener"
        variant="caption"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        View in audit log <OpenInNew sx={{ fontSize: 13 }} />
      </Link>
    </Box>
  );
}

export default function EntityHistory({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['entity-history', entityType, entityId],
    queryFn: () => auditService.query({ entity_type: entityType, entity_id: entityId, limit: 100 }),
    enabled: !!entityType && !!entityId,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const rows = data?.data || [];
  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No history yet.</Typography>
        <FullAuditLink entityType={entityType} entityId={entityId} />
      </Box>
    );
  }

  return (
    <>
    <Box
      sx={{
        maxHeight: 400, overflow: 'auto',
        border: 1, borderColor: 'divider', borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {rows.map((row) => (
        <HistoryRow key={row.id} row={row} />
      ))}
    </Box>
    <FullAuditLink entityType={entityType} entityId={entityId} />
    </>
  );
}

// ---------------------------------------------------------------------------
// HistoryButton — reusable button + dialog wrapper for entity edit forms
// ---------------------------------------------------------------------------

export function HistoryButton({
  entityType, entityId, entityName,
}: { entityType: string; entityId: string; entityName?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title="History">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>History{entityName ? ` — ${entityName}` : ''}</DialogTitle>
        <DialogContent dividers>
          {open && <EntityHistory entityType={entityType} entityId={entityId} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
